import { useEffect, useState } from "react"
import styled from "styled-components"
import Input from "../components/input"
import GooglePlacesAutocomplete from "../components/GooglePlacesAutocomplete"
import Brand from "../components/Brand"
import CocaColaWelcome from "../components/CocaColaWelcome"
import useSignup from "../hooks/useSignup"
import { useHistory, useParams } from "react-router-dom"
import Modal from "../components/Modal"
import LegalConsent from "../components/LegalConsent"
import { useToast } from "../context/ToastContext"
import apiClient from "../helpers/apiClient"
import { FEATURE_FLAGS } from "../config/featureFlags"
import { getHeaderTheme } from "../config/headerTheme"

const SignupPage = (props) => {
  const { setName, setLastName, setEmail, setResto, register, goToLogin, setType, successAcount, pendingApproval, name, lastName, email, resto, loading } = useSignup()
  const { id } = useParams()
  const history = useHistory()
  const toast = useToast()
  const api = apiClient()
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [inviteSent, setInviteSent] = useState(false)
  const [inviteAdminName, setInviteAdminName] = useState("")
  const [inviteAdminEmail, setInviteAdminEmail] = useState("")
  const [inviteRestoName, setInviteRestoName] = useState("")
  const [inviteRestoAddress, setInviteRestoAddress] = useState("")
  const [inviteRestoCountry, setInviteRestoCountry] = useState("")
  const [inviteLoading, setInviteLoading] = useState(false)
  const [acceptedLegal, setAcceptedLegal] = useState(false)

  useEffect(() => {
    setType(id)
    setAcceptedLegal(false)
  }, [id, setType])

  const closeInviteModal = () => {
    if (inviteLoading) return
    setInviteModalOpen(false)
    setInviteSent(false)
  }

  const onRegister = () => {
    if (!acceptedLegal) {
      toast.error(
        id === "resto"
          ? "Debes aceptar los términos del servicio para tiendas y la política de privacidad."
          : "Debes aceptar los términos de participación y la política de privacidad."
      )
      return
    }

    register({
      onRestoNotFound: ({ resto: selectedResto } = {}) => {
        const fallbackResto = selectedResto || resto || {}
        setInviteRestoName(fallbackResto?.name || "")
        setInviteAdminName("")
        setInviteAdminEmail("")
        setInviteRestoAddress(fallbackResto?.formatted_address || "")
        setInviteRestoCountry(fallbackResto?.country || "")
        setInviteSent(false)
        setInviteModalOpen(true)
      }
    })
  }

  const onSendInvite = async () => {
    if (inviteLoading) return

    if (!inviteAdminEmail || !inviteRestoName || !inviteAdminName) {
      toast.error("Completa correo de la tienda, nombre de la tienda y nombre del administrador.")
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(inviteAdminEmail)) {
      toast.error("Ingresa un correo de la tienda válido.")
      return
    }

    try {
      setInviteLoading(true)

      const payload = {
        hostName: (name || "").trim(),
        hostLastName: (lastName || "").trim(),
        adminName: inviteAdminName.trim(),
        adminEmail: inviteAdminEmail.trim(),
        restaurantName: inviteRestoName.trim(),
      }

      const restaurantAddress = inviteRestoAddress.trim()
      const restaurantCountry = inviteRestoCountry.trim()

      if (restaurantAddress) payload.restaurantAddress = restaurantAddress
      if (restaurantCountry) payload.restaurantCountry = restaurantCountry

      const response = await api.post(`/api/v2/auth/restaurant-onboarding-request`, payload)
      toast.success(response?.msg || "Solicitud de onboarding enviada exitosamente.")
      setInviteSent(true)
    } catch (error) {
      toast.error(error?.message || "No se pudo enviar la solicitud. Inténtalo de nuevo.")
    } finally {
      setInviteLoading(false)
    }
  }

  const infoSuccess = {
    "host": {
      "title": "¡Tu cuenta ha sido creada!",
      "desc": "Hemos enviado un correo con el enlace de verificación a tu dirección registrada.",
      "desc2": "Verifica tu cuenta para continuar.",
      "desc3": "Tu perfil necesita ser aprobado por la tienda seleccionada."
    },
    "resto": {
      "title": "¡Tu cuenta ha sido creada y tu tienda ha sido registrada!",
      "desc": "Te enviamos un correo con el enlace de verificación.",
      "desc2": "Verifica tu cuenta para continuar.",
      "desc3": "Tu cuenta debe ser aprobada para iniciar sesión."
    },
    "almacen": {
      "title": "¡Tu cuenta ha sido creada y tu tienda ha sido registrada!",
      "desc": "Te enviamos un correo con el enlace de verificación.",
      "desc2": "Verifica tu cuenta para continuar.",
      "desc3": "Tu cuenta debe ser aprobada para iniciar sesión."
    }
  }

  const infoRegister = {
    "host": {
      "title": "Destácate con TiendIA",
      "desc": "Regístrate y forma parte de la comunidad TiendIA."
    },
    "resto": {
      "title": "Reconoce y motiva a tu equipo con TiendIA",
      "desc": "Únete a TiendIA y gestiona a tu equipo de tenderos."
    },
    "almacen": {
      "title": "Registra tu tienda en TiendIA",
      "desc": "Crea tu cuenta para acceder al panel de gestión de tu tienda."
    }
  }

  const infoPendingApproval = {
    "title": "Tu tienda está pendiente de aprobación",
    "desc": "Tu registro fue creado correctamente.",
    "desc2": "Antes de iniciar sesión, el equipo debe aprobar tu tienda.",
    "desc3": "Te avisaremos cuando la aprobación esté lista."
  }

  return (
    <>
      <Container>
        <BrandWrap>
          {/* Logo Coca-Cola oculto en registro para campaña ALMA */}
          <Brand type="almacen" width="300" height="64" showCokeLogo={false} />
        </BrandWrap>
        {successAcount ? (
          <SuccessContainer>
            <Title>{pendingApproval ? infoPendingApproval.title : (infoSuccess[id]?.title || "")}</Title>
            <Paragraph>{pendingApproval ? infoPendingApproval.desc : (infoSuccess[id]?.desc || "")}</Paragraph>
            <Paragraph>{pendingApproval ? infoPendingApproval.desc2 : (infoSuccess[id]?.desc2 || "")}</Paragraph>
            <Paragraph>{pendingApproval ? infoPendingApproval.desc3 : (infoSuccess[id]?.desc3 || "")}</Paragraph>
            <StyledButton onClick={goToLogin} text="Iniciar sesión" />
          </SuccessContainer>
        ) : (
          <FormContainer>
            <FormTitle>{infoRegister[id]?.title || ""}</FormTitle>
            <Paragraph>{infoRegister[id]?.desc || ""}</Paragraph>
            <Spacer />
            <Label>Nombre</Label>
            <Input label="Nombre" value={name} onChange={(e) => setName(e.target.value)} disabled={loading} />
            <Label>Apellido</Label>
            <Input label="Apellido" value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={loading} />
            <Label>Correo</Label>
            <Input label="Correo" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} />
            <Label>Encuentra tu tienda</Label>
            <div style={{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.6 : 1 }}>
              <GooglePlacesAutocomplete onPlaceSelected={setResto} styleInput={{ background: "#F4F7FE", border: "none" }} />
            </div>
            <LegalConsent
              audience={id === "resto" || id === "almacen" ? "resto" : "host"}
              checked={acceptedLegal}
              onChange={setAcceptedLegal}
              disabled={loading}
            />
            <StyledButton type={id} onClick={onRegister} disabled={loading}>
              {loading ? "Cargando..." : "Registrar"}
            </StyledButton>
          </FormContainer>
        )}

        {FEATURE_FLAGS.SPONSOR_BRANDING_ENABLED && <CocaColaWelcome />}
      </Container>
      <Modal
        isOpen={inviteModalOpen && id === "host"}
        onClose={inviteSent ? () => history.push("/") : closeInviteModal}
        label={inviteSent ? "Solicitud enviada" : "Tu tienda aún no existe en TiendIA"}
        acceptButtonText={inviteSent ? "Volver al inicio" : (inviteLoading ? "Enviando..." : "Enviar solicitud")}
        cancelButtonText={inviteLoading ? "Espera..." : "Cancelar"}
        onAccept={inviteSent ? () => history.push("/") : onSendInvite}
        onCancel={closeInviteModal}
        cancelButton={!inviteSent}
      >
        {inviteSent ? (
          <InviteSuccess>
            <InviteText>
              Enviamos la solicitud para registrar la tienda y continuar con el alta del tendero.
            </InviteText>
            <InviteText>
              Te avisaremos cuando el proceso esté listo.
            </InviteText>
          </InviteSuccess>
        ) : (
          <>
            <InviteText>
              Puedes invitar a la tienda a registrarse en TiendIA. No se registrará tu cuenta de tendero hasta que exista la tienda.
            </InviteText>
            <InviteField>
              <InviteLabel>Nombre del administrador</InviteLabel>
              <InviteInput
                type="text"
                value={inviteAdminName}
                onChange={(e) => setInviteAdminName(e.target.value)}
                placeholder="Nombre del administrador"
                disabled={inviteLoading}
              />
            </InviteField>
            <InviteField>
              <InviteLabel>Correo de la tienda</InviteLabel>
              <InviteInput
                type="email"
                value={inviteAdminEmail}
                onChange={(e) => setInviteAdminEmail(e.target.value)}
                placeholder="admin@tienda.com"
                disabled={inviteLoading}
              />
            </InviteField>
            <InviteField>
              <InviteLabel>Nombre de la tienda</InviteLabel>
              <InviteInput
                type="text"
                value={inviteRestoName}
                onChange={(e) => setInviteRestoName(e.target.value)}
                placeholder="Nombre de la tienda"
                disabled
                readOnly
              />
            </InviteField>
            <InviteField>
              <InviteLabel>Dirección de la tienda (opcional)</InviteLabel>
              <InviteInput
                type="text"
                value={inviteRestoAddress}
                onChange={(e) => setInviteRestoAddress(e.target.value)}
                placeholder="Av. Principal 123"
                disabled
                readOnly
              />
            </InviteField>
            <InviteField>
              <InviteLabel>País de la tienda (opcional)</InviteLabel>
              <InviteInput
                type="text"
                value={inviteRestoCountry}
                onChange={(e) => setInviteRestoCountry(e.target.value)}
                placeholder="México"
                disabled
                readOnly
              />
            </InviteField>
          </>
        )}
      </Modal>
    </>
  )
}

export default SignupPage

/* ======== Styled Components ======== */

const Container = styled.div`
  padding: clamp(12px, 4vw, 24px);
  width: 100%;
  margin: 0 auto;
`

const BrandWrap = styled.div`
  margin-bottom: clamp(16px, 4vw, 22px);

  > div {
    min-height: clamp(104px, 16vw, 132px);
  }
`

const SuccessContainer = styled.div`
  padding: clamp(28px, 6vw, 40px) clamp(8px, 4vw, 20px);
  text-align: center;
`

const FormContainer = styled.div`
  padding: clamp(12px, 4vw, 20px);
  text-align: center;
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 10px;
`

const FormTitle = styled.h2`
  font-size: clamp(21px, 3vw, 24px);
  font-weight: 600;
  text-align: center;
  margin-bottom: 10px;
  line-height: 1.25;
`

const Paragraph = styled.p`
  font-size: 14px;
  text-align: center;
  margin: 5px 0;
  line-height: 1.4;
`

const Spacer = styled.div`
  height: 20px;
`

const Label = styled.label`
  display: block;
  text-align: left;
  font-weight: 500;
  margin: 10px 0 5px 5px;
  font-size: 14px;
`

const theme = getHeaderTheme();

const StyledButton = styled.button`
  border-radius: 100px;
  width: 100%;
  background: ${({ type }) => type === "host" ? theme.primary : "#000000"};
  border: ${({ type }) => type === "host" ? "2px solid transparent" : "2px solid #000000"};
  color: white;
  font-weight: 600;
  font-size: 15px;
  min-height: 52px;
  padding: 14px 16px;
  margin-top: clamp(28px, 6vw, 40px);
  cursor: pointer;
  transition: filter 0.3s ease;
  text-align: center;

  &:hover {
    filter: brightness(0.92);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const InviteText = styled.p`
  font-size: 14px;
  line-height: 1.4;
  margin: 0 0 18px 0;
  color: #333;
`

const InviteSuccess = styled.div`
  text-align: center;
  padding: 12px 0;
`

const InviteField = styled.div`
  margin-bottom: 12px;
`

const InviteLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
`

const InviteInput = styled.input`
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${theme.accent};
  }
`
